import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { date, fine, money, payWords, plural } from '../../utils/format';
import { ErrorNotice, Loading } from '../../components/ui';

interface Receipt {
  receipt_no: string;
  chit_no: string;
  scheme_name: string;
  scheme_type: 'AMOUNT' | 'WEIGHT';
  instalments: number;
  instalment_no: number;
  customer_name: string;
  customer_phone: string | null;
  branch_name: string;
  paid_amount: string;
  paid_on: string;
  pay_mode: string;
  reference_no: string | null;
  rate_per_gram: string | null;
  weight_gms: string | null;
  total_paid: string;
  total_weight: string;
  paid_count: number;
  received_by_name: string | null;
}

/** Rupees in words, the Indian way (lakh, crore), for the receipt. */
function inWords(amount: number): string {
  const ones = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (n: number): string =>
    n < 20 ? ones[n]! : `${tens[Math.floor(n / 10)]}${n % 10 ? ` ${ones[n % 10]}` : ''}`;
  const three = (n: number): string =>
    n >= 100 ? `${ones[Math.floor(n / 100)]} Hundred${n % 100 ? ` ${two(n % 100)}` : ''}` : two(n);
  let n = Math.round(amount);
  if (n === 0) return 'Zero';
  const parts: string[] = [];
  const crore = Math.floor(n / 1e7);
  n %= 1e7;
  const lakh = Math.floor(n / 1e5);
  n %= 1e5;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  if (crore) parts.push(`${two(crore)} Crore`);
  if (lakh) parts.push(`${two(lakh)} Lakh`);
  if (thousand) parts.push(`${two(thousand)} Thousand`);
  if (n) parts.push(three(n));
  return parts.join(' ');
}

/**
 * A savings instalment receipt — what the customer takes home as proof of
 * payment. It shows the running total as well as this payment, because
 * "how much have I saved so far?" is the question every customer asks.
 */
export function ChitReceiptPage() {
  const { id, no } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['chit-receipt', id, no],
    queryFn: () => api.get<Receipt>(`/chit/accounts/${id}/receipts/${no}`),
  });
  const { header } = usePrintHeader(data?.branch_name);

  if (isLoading) return <Loading rows={6} />;
  if (error || !data) return <ErrorNotice error={error} />;
  const r = data;
  const address = header.branchAddress;

  return (
    <>
      <div className="no-print mb-4 flex gap-2">
        <button className="btn-ghost" onClick={() => navigate(-1)}>
          Back
        </button>
        <button
          className="btn-primary"
          onClick={() => {
            const was = document.title;
            document.title = r.receipt_no.replace(/[\\/:*?"<>|]/g, '-');
            window.print();
            window.setTimeout(() => {
              document.title = was;
            }, 1000);
          }}
        >
          Print receipt
        </button>
      </div>

      <div className="print-slip">
        <div className="border-b-2 border-gold-600 pb-3 text-center">
          {header.logoDataUri ? (
            <img src={header.logoDataUri} alt="" className="mx-auto mb-1 h-12 object-contain" />
          ) : null}
          <p className="text-lg font-bold">{header.companyName}</p>
          {address ? <p className="text-2xs text-ink-soft">{address}</p> : null}
          {header.branchPhone ? <p className="num text-2xs text-ink-soft">Ph {header.branchPhone}</p> : null}
          {header.companyGstin ? <p className="num text-2xs text-ink-soft">GSTIN {header.companyGstin}</p> : null}
          <p className="mt-2 inline-block rounded border border-ink/30 px-3 py-0.5 text-xs font-bold uppercase tracking-wider">
            Savings instalment receipt
          </p>
        </div>

        <div className="mt-3 flex justify-between text-sm">
          <span>
            Receipt <span className="num font-semibold">{r.receipt_no}</span>
          </span>
          <span className="num">{date(r.paid_on)}</span>
        </div>

        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-ink-soft">Received from</dt>
          <dd className="text-right font-medium">{r.customer_name}</dd>
          {r.customer_phone ? (
            <>
              <dt className="text-ink-soft">Mobile</dt>
              <dd className="num text-right">+91 {r.customer_phone}</dd>
            </>
          ) : null}
          <dt className="text-ink-soft">Scheme</dt>
          <dd className="text-right">{r.scheme_name}</dd>
          <dt className="text-ink-soft">Chit no.</dt>
          <dd className="num text-right">{r.chit_no}</dd>
          <dt className="text-ink-soft">Instalment</dt>
          <dd className="num text-right">
            {r.instalment_no} of {r.instalments}
          </dd>
          <dt className="text-ink-soft">Paid by</dt>
          <dd className="text-right">
            {payWords(r.pay_mode)}
            {r.reference_no ? <span className="num"> · {r.reference_no}</span> : null}
          </dd>
        </dl>

        <div className="mt-4 rounded-lg border border-gold-200 bg-gold-50 p-3 text-center">
          <p className="num text-2xl font-bold">{money(r.paid_amount)}</p>
          <p className="text-2xs text-ink-soft">Rupees {inWords(Number(r.paid_amount))} only</p>
          {r.weight_gms ? (
            <p className="mt-1 text-sm">
              = <span className="num font-semibold">{fine(r.weight_gms)}</span> gold at{' '}
              <span className="num">{money(r.rate_per_gram)}</span>/g
            </p>
          ) : null}
        </div>

        <div className="mt-3 flex justify-between border-t border-dashed border-silver-400 pt-2 text-sm">
          <span className="text-ink-soft">Saved so far ({plural(r.paid_count, 'instalment')})</span>
          <span className="num font-semibold">{money(r.total_paid)}</span>
        </div>
        {r.scheme_type === 'WEIGHT' ? (
          <div className="flex justify-between text-sm">
            <span className="text-ink-soft">Gold saved so far</span>
            <span className="num font-semibold">{fine(r.total_weight)}</span>
          </div>
        ) : null}

        <div className="mt-10 flex justify-between gap-6 text-2xs text-ink-soft">
          <div className="w-36">
            <div className="mb-1 h-8 border-b border-ink/40" />
            Received by{r.received_by_name ? ` ${r.received_by_name}` : ''}
          </div>
          <div className="w-36 text-right">
            <div className="mb-1 h-8 border-b border-ink/40" />
            For {header.companyName}
          </div>
        </div>
        <p className="mt-4 text-center text-2xs text-ink-faint">
          This is a savings deposit, not a sale. Keep this receipt with your passbook.
        </p>
      </div>
    </>
  );
}
